import React from 'react';
import * as R from 'ramda';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pure, branch, compose, withState, withProps, withHandlers, renderNothing } from 'react-recompose';
// components
import { PageTitle } from '../page-title';
import { Label as LabelComponent } from '../label';
import { closeModal, openModal } from '../modal/actions';
// features
import PC from '../../features/permission/role-permission';
import { makeSelectCurrentUserName } from '../../features/auth/selectors';
import { makeSelectRoleAvailableList } from '../../features/role/selectors';
import { makeSelectInitialDataLoadedStatus } from '../../features/permission/selectors';
import { makeSelectCrossDockLocationOptions } from '../../features/dispatch-board/selectors';
import { makeSelectAvailableReportGeoFencingZoneList } from '../../features/geo-fencing-zone/report/selectors';
import {
  makeSelectAccessorialsList,
  makeSelectFleetDocumentAndCertificationTypes,
} from '../../features/configurations/selectors';
import {
  makeSelectLoadEventRefTypes,
  makeSelectAllAvailableCloReferenceTypes,
  makeSelectAllAvailableTelReferenceTypes,
  makeSelectAvailableReferenceTypesByScope,
  makeSelectAllAvailableClosAndTelsReferenceTypes,
  makeSelectAllAvailableReferenceTypesByScopeName,
  makeSelectAllAvailableCloAndCloInvoiceReferenceTypes,
} from '../../features/reference/selectors';
// forms
import { Label, Checkbox } from '../../forms/ui';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// hocs
import { withComponentDidUpdatePropCallback } from '../../hocs/common';
// icons
import * as I from '../../svgs';
// ui
import {
  Box,
  Flex,
  RelativeFlex,
  StickedWrapper,
  ModalActionButton,
  ModalCancelButton,
  scrollableContainerCss3px,
} from '../../ui';
// component edit-report
import SortList from './components/sort-list';
import FieldList from './components/field-list';
import FilterList from './components/filter-list';
import { ReportForm } from './components/report-form';
import ReportSummary from './components/report-summary';
import { ReportFormButtons } from './components/form-buttons';
import { withReportSortList } from './components/sort-list/hocs';
import { withReportFieldList } from './components/field-list/hocs';
import { withReportFilterList } from './components/filter-list/hocs';
import { makeSelectRequestStatus, makeSelectUsedReportStatus } from './selectors';
import { buttons, buildReport, getAssessorials, isNotReportValid, getAvailableFilterFields } from './helpers';
//////////////////////////////////////////////////

const setEditReportFormProps = ({ usedReport, availableFields, showReportSummary }) => {
  const {
    name,
    prompt,
    fields,
    roleGuids,
    orderFields,
    description,
    customerGuids,
    defaultReport,
  } = R.or(usedReport, {});


  const reportFields = R.sort(
    (elem1, elem2) => R.subtract(elem1.sequence, elem2.sequence),
    R.filter(
      ({ name }) => G.notEquals(name, GC.FIELD_GUID),
      R.or(fields, []),
    ),
  );

  const sortFields = R.sort(
    (elem1, elem2) => R.subtract(elem1.sequence, elem2.sequence),
    R.filter(
      ({ name }) => (
        R.or(
          G.notEquals(name, 'roles'),
          G.notEquals(name, GC.FIELD_GUID),
        )
      ), R.or(orderFields, []),
    ),
  );

  const filterFields = R.filter(
    ({ name }) => G.notEquals(name, GC.FIELD_GUID),
    G.getOrElse(usedReport, 'searchCriteria', []),
  );


  let initialValues = {
    name,
    roleGuids,
    description,
    customerGuids,
    defaultReport,
  };

  if (G.isTrue(showReportSummary)) {
    initialValues = {
      ...initialValues,
      [GC.FIELD_GROUP_BY]: R.pathOr(null, [GC.FIELD_GROUP_BY], usedReport),
      [GC.FIELD_SUMMARY_GROUP]: R.pathOr(null, [GC.FIELD_SUMMARY_GROUP], usedReport),
      [GC.FIELD_SHOW_LAST_DAYS]: R.pathOr(null, [GC.FIELD_SHOW_LAST_DAYS], usedReport),
      [GC.FIELD_SUMMARY_SUBGROUP]: R.pathOr(null, [GC.FIELD_SUMMARY_SUBGROUP], usedReport),
      [GC.FIELD_GROUP_BY_SORT]: R.pathOr(GC.SORT_TYPE_DESC, [GC.FIELD_GROUP_BY_SORT], usedReport),
    };
  }

  return {
    sortFields,
    reportFields,
    filterFields,
    initialValues,
    availableFields,
    prompt: R.or(prompt, false),
  };
};

const reportEnhancer = compose(
  withState('prompt', 'setPrompt', ({ prompt }) => prompt),
  withHandlers({
    handleCreateReport: (props) => (values) => {
      const { closeModal, createReportRequest } = props;

      const newReport = {
        ...buildReport({ ...props, isCreate: true }),
        ...values,
        guid: null,
        version: null,
      };

      if (isNotReportValid(newReport)) return;

      createReportRequest(newReport, { prepareUsedReportForPost: true });

      G.callFunctionTwice(closeModal);
    },
    handleEditReport: (props) => (values) => {
      const { closeModal, updateReportRequest } = props;

      const newReport = { ...buildReport({ ...props, isUpdate: true }), ...values };

      if (isNotReportValid(newReport)) return;

      updateReportRequest(newReport, { prepareUsedReportForPost: true });

      G.callFunctionTwice(closeModal);
    },
  }),
);

export const enhance = compose(
  withState('formValues', 'setFormValues', R.propOr({}, 'initialValues')),
  withState('showIssues', 'setShowIssues', R.pathOr(false, ['usedReport', 'showIssues'])),
  withState(
    GC.FIELD_AVOID_RELATED_VALUE_DUPLICATES,
    'setAvoidRelatedValueDuplicates',
    R.pathOr(false, ['usedReport', GC.FIELD_AVOID_RELATED_VALUE_DUPLICATES]),
  ),
  withProps((props) => {
    const {
      fields,
      disableRefFilter,
      allAvailableRefTypesByScopeName,
      fleetDocumentAndCertificationTypes,
    } = props;

    const reportType = R.path(['usedReport', GC.FIELD_TYPE], props);

    const filterSettings = {
      reportType,
      assessorialList: getAssessorials(props),
      disableRefFilter,
      fleetDocumentAndCertificationTypes,
    };

    const referenceFields = G.getRefListByReportType(allAvailableRefTypesByScopeName, reportType);
    const reportFieldOptions = R.prop('reportFieldOptions', props);

    const availableFields = G.setFilterByParams(
      referenceFields,
      fields,
      filterSettings,
    );

    return ({
      availableFields,
      availableReportFields: G.isNotNil(reportFieldOptions) ? reportFieldOptions : availableFields,
    });
  }),
  withProps((props) => setEditReportFormProps(props)),
  withReportSortList,
  withReportFieldList,
  withReportFilterList,
  branch(
    ({ requestPending, initialDataLoaded }) => (
      R.or(
        R.not(initialDataLoaded),
        G.isTrue(requestPending),
      )
    ),
    renderNothing,
  ),
  reportEnhancer,
  withHandlers({
    handlePrompt: ({ prompt, setPrompt }) => () => {
      setPrompt(R.not(prompt));
    },
    handleUseAction: (props) => () => {
      const { setReport, closeModal, onReportSet } = props;

      const newReport = R.assoc('isDraft', true, buildReport(props));

      if (isNotReportValid(newReport)) return;

      const submitSettings = { prepareUsedReportForPost: false };

      if (G.isFunction(setReport)) setReport(newReport, submitSettings);

      if (G.isFunction(onReportSet)) onReportSet(newReport, submitSettings);

      closeModal();
    },
    handleClickSave: (props) => () => {
      const {
        itemList,
        openModal,
        formValues,
        originParams,
        initialValues,
        usedReportStatus,
        handleEditReport,
      } = props;

      const originData = G.ifElse(usedReportStatus, originParams, R.mergeRight(initialValues, formValues));

      const component = (
        <ReportForm
          roles={R.values(itemList)}
          initialValues={originData}
          submitAction={handleEditReport}
        />
      );

      const modal = {
        p: 15,
        component,
        options: {
          width: 400,
          overflow: 'auto',
          maxHeight: '80vh',
          title: G.getWindowLocale('actions:edit-report', 'Edit Report'),
        },
      };

      openModal(modal);
    },
    handleClickSaveAsNew: ({ itemList, openModal, createNewReportText, handleCreateReport }) => () => {
      const component = <ReportForm roles={R.values(itemList)} submitAction={handleCreateReport} />;

      const modal = {
        p: 15,
        component,
        options: {
          width: 400,
          overflow: 'auto',
          maxHeight: '80vh',
          title: R.or(createNewReportText, G.getWindowLocale('titles:create-new-report', 'Create New Report')),
        },
      };

      openModal(modal);
    },
    handleClickCancel: ({ closeModal }) => () => closeModal(),
    setSubmitButtonStatus: ({ usedReport, userLoginId }) => () => {
      const { owner } = usedReport;

      const reportGuid = G.getOrElse(usedReport, GC.FIELD_GUID, '');

      return R.or(
        G.notEquals(owner, userLoginId),
        R.or(R.isEmpty(reportGuid), reportGuid.includes('Default')),
      );
    },
  }),
  pure,
);

const EditReportForm = (props) => {
  const {
    hasIssues,
    showIssues,
    usedReport,
    hideUseBtn,
    setShowIssues,
    hideSaveAsNew,
    editReportText,
    showIssuesLabel,
    handleUseAction,
    updateReportText,
    showReportSummary,
    disableSortByFields,
    disableFilterByFields,
    availableReportFields,
    setSubmitButtonStatus,
    disableSetReportFields,
    AlternativeReportButtons,
    avoidRelatedValueDuplicates,
    setAvoidRelatedValueDuplicates,
  } = props;

  const usedReportName = G.getPropFromObject(GC.FIELD_NAME, usedReport);

  const reportName = G.ifElse(
    G.isTrue(G.getPropFromObject('isDraft', usedReport)),
    `${G.getWindowLocale('titles:draft', 'Draft')} (${usedReportName})`,
    usedReportName,
  );

  const availableForFilter = getAvailableFilterFields(props);

  const isCustomerInvoiceReport = R.propEq(GC.CUSTOMER_INVOICE_REPORT, GC.FIELD_TYPE, usedReport);
  const isPivotCustomerInvoiceReport = R.propEq(GC.PIVOT_CUSTOMER_INVOICE_REPORT, GC.FIELD_TYPE, usedReport);

  return (
    <Box
      id='edit_report_wrapper'
      height='calc(100vh - 135px)'
      bg={G.getTheme('pages.layOutBgColor')}
    >
      <StickedWrapper left='0px' top='0px' bgColor='white' zIndex={12}>
        <Flex>
          <PageTitle
            mb='5px'
            withCount={false}
            title={`${R.or(editReportText, G.getWindowLocale('titles:edit-report', 'Edit Report'))}: ${reportName}`}
          />
          {
            G.isTrue(hasIssues) &&
            <Flex m='0 0 15px 15px'>
              <Checkbox
                id='issues'
                type='checkbox'
                checked={showIssues}
                onChange={() => setShowIssues(R.not(showIssues))}
              />
              <Label ml={10} htmlFor='issues' cursor='pointer'>
                {
                  R.or(showIssuesLabel, G.getWindowLocale('titles:show-notifications', 'Show Notifications'))
                }
              </Label>
            </Flex>
          }
          {
            R.or(isCustomerInvoiceReport, isPivotCustomerInvoiceReport) &&
            <Flex m='0 0 15px 15px'>
              <Checkbox
                type='checkbox'
                checked={avoidRelatedValueDuplicates}
                id={GC.FIELD_AVOID_RELATED_VALUE_DUPLICATES}
                onChange={() => setAvoidRelatedValueDuplicates(R.not(avoidRelatedValueDuplicates))}
              />
              <Label ml={10} cursor='pointer' htmlFor={GC.FIELD_AVOID_RELATED_VALUE_DUPLICATES}>
                {
                  G.ifElse(
                    isCustomerInvoiceReport,
                    G.getWindowLocale(
                      'titles:avoid-related-number-duplicates-excel',
                      'Avoid Related Number Duplicates In Excel',
                    ),
                    G.getWindowLocale(
                      'titles:avoid-related-number-duplicates',
                      'Avoid Related Number Duplicates',
                    ),
                  )
                }
              </Label>
            </Flex>
          }
        </Flex>
      </StickedWrapper>
      <Flex
        zIndex={1}
        width='100%'
        overflowY='visible'
        borderTop='1px solid'
        alignItems='flex-start'
        maxHeight='calc(100vh - 200px)'
        borderColor={G.getTheme('colors.lightGrey')}
      >
        {
          R.not(G.isTrue(disableSetReportFields)) && (
            <FieldList {...props} availableFields={availableReportFields} />
          )
        }
        <Box
          minWidth={0}
          maxWidth='100%'
          flex='1 1 auto'
          overflowX='auto'
          overflowY='visible'
          css={scrollableContainerCss3px}
        >
          <RelativeFlex
            zIndex={1}
            maxWidth={1520}
            width='max-content'
            overflowY='visible'
            alignItems='flex-start'
          >
            <Flex flexDirection='column'>
              {
                showReportSummary && <ReportSummary {...props} />
              }
              {
                showReportSummary &&
                <Box
                  width='1px'
                  zIndex={11}
                  borderLeft='1px solid'
                  bg={G.getTheme('colors.light.mainLight')}
                  borderColor={G.getTheme('colors.lightGrey')}
                />
              }
              {
                R.not(G.isTrue(disableSortByFields)) &&
                <SortList {...props} />
              }
            </Flex>
            {
              R.not(G.isTrue(disableFilterByFields)) &&
              <FilterList
                {...props}
                showAllowEdit={true}
                useMenuPortalTarget={true}
                availableFields={availableForFilter}
              />
            }
          </RelativeFlex>
        </Box>
      </Flex>
      {R.isNil(AlternativeReportButtons) && (
        <ReportFormButtons
          {...props}
          pl={15}
          width='auto'
          style={{ position: 'absolute' }}
          submitting={setSubmitButtonStatus()}
          showSaveAsNew={R.not(hideSaveAsNew)}
          buttons={G.ifElse(G.isTrue(hideUseBtn), [], buttons(handleUseAction))}
          btnText={R.or(updateReportText, G.getWindowLocale('actions:update-report', 'Update Report'))}
        />
      )}
      {G.isNotNil(AlternativeReportButtons) && (
        <AlternativeReportButtons
          {...props}
          pl={15}
          width='auto'
          style={{ position: 'absolute' }}
        />
      )}
    </Box>
  );
};

const mapGeoFencingProps = (state) => createStructuredSelector({
  geoFencingZones: makeSelectAvailableReportGeoFencingZoneList(state),
});

const outsideReportEnhance = compose(
  connect(mapGeoFencingProps),
  withReportFilterList,
  withHandlers({
    handleAddFiltersGroupOnDidMount: (props) => () => {
      const { reportFilterGroups, handleAddFiltersGroup, reportSimpleFiltersGroupId } = props;

      if (G.hasNotAmousCurrentUserPermissions(PC.REPORT_WRITE)) return;

      if (G.isNilOrEmpty(G.getPropFromObject(reportSimpleFiltersGroupId, reportFilterGroups))) {
        handleAddFiltersGroup();
      }
    },
    handleUseReport: (props) => () => {
      const {
        usedReport,
        closePopper,
        setUsedReport,
        handleListRequest,
        reportFilterGroups,
        reportSimpleFiltersGroupId,
      } = props;

      const exactFilters = R.map(
        (item) => R.assoc('groupId', null, item),
        R.pathOr([], [reportSimpleFiltersGroupId], reportFilterGroups),
      );

      const newReport = {
        ...usedReport,
        isDraft: true,
        searchCriteria: exactFilters,
      };

      if (isNotReportValid(newReport)) return;

      setUsedReport(newReport);

      handleListRequest(true);
      closePopper();
    },
    handleCleanFilters: (props) => () => {
      const { reportFilterGroups, setReportFilterGroups, reportSimpleFiltersGroupId } = props;

      setReportFilterGroups(R.assoc(reportSimpleFiltersGroupId, [], reportFilterGroups));
    },
    handleCloseFilterPopup: (props) => () => {
      const {
        usedReport,
        closePopper,
        setUsedReport,
        handleListRequest,
        reportFilterGroups,
        reportSimpleFiltersGroupId,
      } = props;

      const exactFilters = G.getPropFromObject(reportSimpleFiltersGroupId, reportFilterGroups);

      if (G.isNilOrEmpty(exactFilters)) {
        const newReport = {
          ...usedReport,
          isDraft: true,
          searchCriteria: [],
        };

        setUsedReport(newReport);

        handleListRequest(true);
        closePopper();
      }
    },
  }),
  withComponentDidUpdatePropCallback({
    propName: 'reportSimpleFiltersGroupId',
    callbackName: 'handleAddFiltersGroupOnDidMount',
  }),
  withComponentDidUpdatePropCallback({
    propName: 'reportFilterGroups',
    callbackName: 'handleCloseFilterPopup',
  }),
  pure,
);

export const OutsideReportExactFilterList = outsideReportEnhance((props) => {
  const {
    closePopper,
    handleUseReport,
    handleCleanFilters,
    reportFilterGroups,
    filterPopupMinWidth,
    reportSimpleFiltersGroupId,
  } = props;

  const withoutReportWritePermission = G.hasNotAmousCurrentUserPermissions(PC.REPORT_WRITE);

  return (
    <Box minWidth={R.or(filterPopupMinWidth, 620)}>
      <FilterList
        {...props}
        useMenuPortalTarget={true}
        availableFields={getAvailableFilterFields(props)}
        withoutReportWritePermission={withoutReportWritePermission}
        reportFilterGroups={R.pick([reportSimpleFiltersGroupId], reportFilterGroups)}
      />
      <Flex m={10} justifyContent='flex-end'>
        {
          R.not(withoutReportWritePermission) &&
          <ModalCancelButton height={30} type='button' minWidth={160} onClick={handleCleanFilters}>
            {G.getWindowLocale('actions:clean-and-close', 'Clean And Close')}
          </ModalCancelButton>
        }
        <ModalCancelButton mx={15} height={30} type='button' onClick={closePopper}>
          {G.getWindowLocale('actions:close', 'Close')}
        </ModalCancelButton>
        <ModalActionButton height={30} type='button' onClick={handleUseReport}>
          <LabelComponent fontWeight={700} frontIcon={I.play(G.getTheme('icons.iconColorWhite'))}>
            {G.getWindowLocale('actions:use', 'Use')}
          </LabelComponent>
        </ModalActionButton>
      </Flex>
    </Box>
  );
});

const mapStateToProps = (state) => createStructuredSelector({
  itemList: makeSelectRoleAvailableList(state),
  userLoginId: makeSelectCurrentUserName(state),
  requestPending: makeSelectRequestStatus(state),
  assessorials: makeSelectAccessorialsList(state),
  usedReportStatus: makeSelectUsedReportStatus(state),
  loadEventRefList: makeSelectLoadEventRefTypes(state),
  refList: makeSelectAvailableReferenceTypesByScope(state),
  cloRefList: makeSelectAllAvailableCloReferenceTypes(state),
  telRefList: makeSelectAllAvailableTelReferenceTypes(state),
  initialDataLoaded: makeSelectInitialDataLoadedStatus(state),
  crossDockLocationOptions: makeSelectCrossDockLocationOptions(state),
  geoFencingZones: makeSelectAvailableReportGeoFencingZoneList(state),
  closTelsRefList: makeSelectAllAvailableClosAndTelsReferenceTypes(state),
  cloInvoiceCloRefList: makeSelectAllAvailableCloAndCloInvoiceReferenceTypes(state),
  allAvailableRefTypesByScopeName: makeSelectAllAvailableReferenceTypesByScopeName(state),
  fleetDocumentAndCertificationTypes: makeSelectFleetDocumentAndCertificationTypes(state),
});

export const EditReport = connect(mapStateToProps, {
  openModal,
  closeModal,
})(enhance(EditReportForm));

export default EditReport;
